import classes from "./IPDPatientList.module.css";
import React, { useState } from "react";
import { Table } from "react-bootstrap";
import Filters from "../Reports/Filters";
import { getReportHeader, getReportStyles } from "../../../util/reportPrintHelper";
import AuthContext from "../../../store/auth-context";
import { useContext } from "react";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { FaEye, FaFileExport, FaPrint } from "react-icons/fa";
import { drawReportHeader, drawAuthorizedSignatory } from "../../../util/headerUtil";

const dummyData = [
  {
    ipdNo: "IPD001",
    patientId: "P101",
    name: "Rohit Sharma",
    gender: "Male",
    phone: "9876543210",
    doctor: "Dr. Mehta",
    floor: "2nd",
    group: "Deluxe",
    bedNo: "B12",
    note: "Diabetes",
    admissionDate: "2025-09-01",
    dischargeDate: "2025-09-10",
    billing: "Yes",
  },
  {
    ipdNo: "IPD002",
    patientId: "P102",
    name: "Anita Deshmukh",
    gender: "Female",
    phone: "9123456780",
    doctor: "Dr. Patil",
    floor: "1st",
    group: "General",
    bedNo: "G08",
    note: "Hypertension",
    admissionDate: "2025-09-05",
    dischargeDate: "",
    billing: "Partial",
  },
  {
    ipdNo: "IPD003",
    patientId: "P103",
    name: "Arjun Kulkarni",
    gender: "Male",
    phone: "9876001122",
    doctor: "Dr. Singh",
    floor: "3rd",
    group: "ICU",
    bedNo: "IC05",
    note: "Critical Care",
    admissionDate: "2025-09-12",
    dischargeDate: "",
    billing: "No",
  },
];

const DischargedPatientList = () => {
  const authCtx = useContext(AuthContext);
  const { hospitalData } = authCtx;
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const [submittedDates, setSubmittedDates] = useState({
    start: getTodayDate(),
    end: getTodayDate(),
  });
  const [selectedPageSize, setSelectedPageSize] = useState("a4");
  const [selectedOrientation, setSelectedOrientation] = useState("portrait");
  const [showPreview, setShowPreview] = useState(null);

  const onGetStartEndDateHandler = (start, end) => {
    setSubmittedDates({ start, end });
  };

  // ✅ Generate PDF
  const generatePDF = () => {
    const pdfDoc = new jsPDF(selectedOrientation, "pt", selectedPageSize);
    const pageWidth = pdfDoc.internal.pageSize.getWidth();
    const title = "Discharged Patient List";

    const textWidth =
      (pdfDoc.getStringUnitWidth(title) * pdfDoc.internal.getFontSize()) /
      pdfDoc.internal.scaleFactor;
    const xPos = (pageWidth - textWidth) / 2;

    const tableClone = document.querySelector("#dischargedPatientsTable").cloneNode(true);
    const actionHeader = tableClone.querySelector("th:last-child");
    if (actionHeader) actionHeader.remove();
    tableClone.querySelectorAll("tr").forEach(tr => {
      const lastCell = tr.querySelector("td:last-child");
      if (lastCell) lastCell.remove();
    });

    const startY = drawReportHeader(pdfDoc, hospitalData, title, { margin: 0, top: 0, hideSeparator: true, isExport: true });

    pdfDoc.autoTable({
      html: tableClone,
      startY: startY + 10,
      styles: {
        fontSize: 8,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        lineWidth: 0.1,
        lineColor: [0, 0, 0]
      },
      footStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        lineWidth: 0.1,
        lineColor: [0, 0, 0]
      },
    });

    drawAuthorizedSignatory(pdfDoc, hospitalData);

    return pdfDoc;
  };

  // ✅ Export as PDF
  const onExportHandler = () => {
    generatePDF().save("discharged-patient-list.pdf");
  };

  // ✅ Preview PDF
  const onPreviewHandler = () => {
    const pdfBlob = generatePDF().output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    setShowPreview(pdfUrl);
  };

  // ✅ Print Table directly
  const onPrintHandler = () => {
    if (!dummyData || dummyData.length === 0) {
      alert("No data to print");
      return;
    }

    // Create a hidden iframe for printing
    let printFrame = document.getElementById("printFrame");
    if (!printFrame) {
      printFrame = document.createElement("iframe");
      printFrame.id = "printFrame";
      printFrame.style.display = "none";
      document.body.appendChild(printFrame);
    }

    const tableClone = document.querySelector("#dischargedPatientsTable").cloneNode(true);

    const printContent = `
      <html>
        <head>
          <title>Discharged Patient List</title>
          <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" rel="stylesheet">
          <style>
            ${getReportStyles(selectedPageSize, selectedOrientation)}
          </style>
        </head>
        <body>
          ${getReportHeader(hospitalData, "Discharged Patient List")}
          <div class="meta-info">
            <div><strong>Date Range:</strong> ${submittedDates.start} to ${submittedDates.end}</div>
            <div><strong>Total Records:</strong> ${dummyData.length}</div>
          </div>
          ${tableClone.outerHTML}
        </body>
      </html>
    `;

    const frameDoc = printFrame.contentWindow.document;
    frameDoc.open();
    frameDoc.write(printContent);
    frameDoc.close();

    printFrame.contentWindow.focus();
    setTimeout(() => {
      printFrame.contentWindow.print();
    }, 500);
  };

  return (
    <div className="container-fluid">
      <h2 className={classes.pageTitle}> Discharged Patient History</h2>
      <Filters onGetStartAndEndDate={onGetStartEndDateHandler} />

      <div className="printData boxes table-responsive">
        <Table id="dischargedPatientsTable" striped bordered hover responsive>
          <thead>
            <tr>
              <th>IPD No</th>
              <th>Patient ID</th>
              <th>Name</th>
              <th>Gender</th>
              <th>Phone</th>
              <th>Doctor</th>
              <th>Floor</th>
              <th>Group</th>
              <th>Bed No</th>
              <th>Admission Date</th>
              <th>Discharge Date</th>
              <th>Billing</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody>
            {dummyData.length > 0 ? (
              dummyData.map((patient) => (
                <tr key={patient.ipdNo}>
                  <td>{patient.ipdNo}</td>
                  <td>{patient.patientId}</td>
                  <td>{patient.name}</td>
                  <td>{patient.gender}</td>
                  <td>{patient.phone}</td>
                  <td>{patient.doctor}</td>
                  <td>{patient.floor}</td>
                  <td>{patient.group}</td>
                  <td>{patient.bedNo}</td>
                  <td>{patient.admissionDate}</td>
                  <td>{patient.dischargeDate}</td>
                  <td>{patient.billing}</td>
                  <td>{patient.note}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="13" className="text-center">
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {dummyData.length > 0 && (
        <div className="row pt-4 pb-4">
          <div className="col-md-3 col-6 tbpad text-right">
            <label>Select Size</label>
            <select
              className="form-select dis-inline-block"
              value={selectedPageSize}
              onChange={(e) => setSelectedPageSize(e.target.value)}
            >
              <option value="a2">A2</option>
              <option value="a3">A3</option>
              <option value="a4">A4</option>
              <option value="a5">A5</option>
            </select>
          </div>

          <div className="col-md-4 col-6 tbpad text-right">
            <label>Select Orientation</label>
            <select
              className="form-select dis-inline-block"
              value={selectedOrientation}
              onChange={(e) => setSelectedOrientation(e.target.value)}
            >
              <option value="portrait">Portrait</option>
              <option value="landscape">Landscape</option>
            </select>
          </div>

          <div className="col-md-1 col-4 tbpad">
            <button onClick={onPreviewHandler} className="btn-premium">
              <FaEye className="me-2" /> Preview
            </button>
          </div>
          <div className="col-md-1 col-4 tbpad">
            <button onClick={onExportHandler} className="btn-export-premium">
              <FaFileExport className="me-2" /> Export
            </button>
          </div>
          <div className="col-md-1 col-4 tbpad">
            <button onClick={onPrintHandler} className="btn-premium">
              <FaPrint className="me-2" /> Print
            </button>
          </div>
        </div>
      )}

      {showPreview && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
          role="dialog"
        >
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowPreview(null)}
                ></button>
              </div>
              <div className="modal-body">
                <iframe
                  title="PDF Preview"
                  src={showPreview}
                  width="100%"
                  height="600"
                  frameBorder="0"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DischargedPatientList;
